import { useEffect, useRef, useState } from 'react';
import { PALETTE } from '../palette.js';
import { api } from '../api.js';
import { loginHrefForCurrentLocation } from '../invite-handoff.js';
import BrandIdentity from '../components/BrandIdentity.jsx';
import { Button, Card, Pill } from '../components/primitives.jsx';
import { IconCheck, IconInfo, IconLogout, IconMail } from '../components/icons.jsx';

function tenantHeaders(companyId) {
  return companyId ? { headers: { 'x-company-id': companyId } } : undefined;
}

function titleCase(value) {
  return String(value || '')
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ') || 'Member';
}

export function safeInviteError(err) {
  const code = String(err?.code || '');
  const message = String(err?.message || 'Invite request failed');
  if (code === 'invite_not_found' || message.includes('invite_not_found')) return 'This invitation is unavailable or may belong to another account.';
  if (code === 'invite_expired' || message.includes('invite_expired')) return 'This invitation has expired. Ask an administrator to send a new invite.';
  if (code === 'invite_email_mismatch' || message.includes('invite_email_mismatch')) return 'This invitation belongs to a different email address.';
  if (code === 'invite_canceled' || message.includes('invite_canceled')) return 'This invitation was canceled. Ask an administrator if access is still required.';
  if (code === 'invite_company_inactive' || message.includes('invite_company_inactive')) return 'This company is not active. Contact an administrator before trying again.';
  if (code === 'invite_already_accepted' || message.includes('invite_already_accepted')) return 'This invitation was already accepted. Sign in and select the company from your account.';
  if (code === 'missing_session' || code === 'unauthorized' || message.includes('missing_session') || message.includes('unauthorized')) return 'Sign in to accept this invitation.';
  return 'The invitation could not be accepted.';
}

export function shouldOfferAccountSwitch(err) {
  return err?.code === 'invite_email_mismatch' || err?.code === 'invite_not_found';
}

export function createInvitationAcceptanceCoordinator(postAccept) {
  const requests = new Map();
  return function acceptOnce(requestKey, invitationId) {
    const existing = requests.get(requestKey);
    if (existing) return existing;

    const request = Promise.resolve()
      .then(() => postAccept(invitationId))
      .catch((error) => {
        if (requests.get(requestKey) === request) requests.delete(requestKey);
        throw error;
      });
    requests.set(requestKey, request);
    return request;
  };
}

const acceptInvitationOnce = createInvitationAcceptanceCoordinator(
  (invitationId) => api.post('/api/saas/invitations/accept', { invitationId }),
);

export function acceptedInviteCompanyId(accepted = {}) {
  const ids = [accepted.activeCompany?.id, accepted.membership?.companyId]
    .filter(Boolean)
    .map(String);
  if (!ids.length || ids.some((id) => id !== ids[0])) return '';
  return ids[0];
}

export function acceptedInviteContextMatches(context = {}, companyId = '') {
  const target = String(companyId || '');
  return Boolean(
    target
    && String(context.activeCompany?.id || '') === target
    && String(context.tenant?.companyId || '') === target
    && typeof context.role === 'string'
    && context.role
    && Array.isArray(context.memberships)
    && context.memberships.some((membership) => String(membership?.companyId || '') === target),
  );
}

export async function loadAcceptedInviteContext(accepted, { request = api.get } = {}) {
  const companyId = acceptedInviteCompanyId(accepted);
  if (!companyId) {
    const error = new Error('Accepted invitation target unavailable');
    error.code = 'invite_context_unavailable';
    throw error;
  }
  const context = await request('/api/saas/context', tenantHeaders(companyId));
  if (!acceptedInviteContextMatches(context, companyId)) {
    const error = new Error('Accepted invitation context mismatch');
    error.code = 'invite_context_mismatch';
    throw error;
  }
  return context;
}

export async function activateAcceptedInviteContext({
  accepted,
  context,
  setActiveTenantContext,
  navigate,
  path,
} = {}) {
  const companyId = acceptedInviteCompanyId(accepted);
  if (
    !acceptedInviteContextMatches(context, companyId)
    || typeof setActiveTenantContext !== 'function'
    || typeof navigate !== 'function'
  ) return false;
  try {
    const activated = await setActiveTenantContext(context);
    if (activated !== true) return false;
    navigate(path);
    return true;
  } catch {
    return false;
  }
}

export default function AcceptInvite({ route, navigate, session }) {
  const invitationId = route.query?.invitation || '';
  const isSaasSession = session.user?.auth?.type === 'supabase';
  const acceptedContextRef = useRef(null);
  const contextBusyRef = useRef(false);
  const continueBusyRef = useRef(false);
  const [status, setStatus] = useState(invitationId ? 'loading' : 'missing');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [continuing, setContinuing] = useState(false);
  const accountKey = String(session.user?.id || session.user?.email || '').toLowerCase();
  const acceptanceRequestKey = `${invitationId}:${accountKey}`;

  useEffect(() => {
    if (!invitationId || !isSaasSession) return;

    let alive = true;
    setStatus('loading');
    setError(null);
    acceptedContextRef.current = null;

    // Effect replay or a policy-verification remount must not turn one
    // invitation into duplicate acceptance writes. Failed account attempts are
    // removed by acceptInvitationOnce so an intentional account switch retries.
    acceptInvitationOnce(acceptanceRequestKey, invitationId)
      .then(async (accepted) => {
        if (!alive) return;
        setResult(accepted);
        try {
          const context = await loadAcceptedInviteContext(accepted);
          if (!alive) return;
          acceptedContextRef.current = context;
          setStatus('accepted');
        } catch (contextError) {
          if (!alive) return;
          setError(contextError);
          setStatus('context-error');
        }
      })
      .catch((err) => {
        if (!alive) return;
        setError(err);
        setStatus('error');
      });

    return () => { alive = false; };
  }, [acceptanceRequestKey, invitationId, isSaasSession]);

  async function retryAcceptedContext() {
    if (contextBusyRef.current || !result) return;
    contextBusyRef.current = true;
    setError(null);
    setStatus('context-loading');
    try {
      const context = await loadAcceptedInviteContext(result);
      acceptedContextRef.current = context;
      setStatus('accepted');
    } catch (contextError) {
      setError(contextError);
      setStatus('context-error');
    } finally {
      contextBusyRef.current = false;
    }
  }

  async function continueWithAcceptedCompany(path) {
    const acceptedContext = acceptedContextRef.current;
    if (continueBusyRef.current) return;
    continueBusyRef.current = true;
    setContinuing(true);
    try {
      const activated = await activateAcceptedInviteContext({
        accepted: result,
        context: acceptedContext,
        setActiveTenantContext: session.setActiveTenantContext,
        navigate,
        path,
      });
      if (!activated) setStatus('context-error');
    } finally {
      continueBusyRef.current = false;
      setContinuing(false);
    }
  }

  let body;
  if (!invitationId) {
    body = (
      <InviteState
        tone="warn"
        icon={<IconInfo size={18} />}
        title="Invite link is missing"
        body="Open the invitation link from your email, or ask an administrator to send a new invite."
      />
    );
  } else if (!isSaasSession) {
    body = (
      <InviteState
        tone="warn"
        icon={<IconMail size={18} />}
        title="SaaS session required"
        body="Sign in with the invited email address to accept this company invitation."
      >
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 20 }}>
          <Button variant="accent" size="md" onClick={goToInviteLogin}>
            <IconMail size={15} /> Sign In
          </Button>
        </div>
      </InviteState>
    );
  } else if (status === 'accepted') {
    body = (
      <InviteState
        tone="live"
        icon={<IconCheck size={18} />}
        title="Invitation accepted"
        body={`You joined ${result?.activeCompany?.name || 'the company'} as ${titleCase(result?.membership?.role)}.`}
      >
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 20 }}>
          <Button variant="accent" size="md" disabled={continuing} onClick={() => continueWithAcceptedCompany('company')}>
            <IconCheck size={15} /> View Company
          </Button>
          <Button variant="ghost" size="md" disabled={continuing} onClick={() => continueWithAcceptedCompany('campaigns')}>
            Continue
          </Button>
        </div>
      </InviteState>
    );
  } else if (status === 'context-error') {
    body = (
      <InviteState
        tone="warn"
        icon={<IconInfo size={18} />}
        title="Company access needs verification"
        body="The invitation was accepted, but the target company context could not be verified. Retry before continuing."
      >
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 20 }}>
          <Button variant="accent" size="md" onClick={retryAcceptedContext}>
            Retry company access
          </Button>
        </div>
      </InviteState>
    );
  } else if (status === 'context-loading') {
    body = (
      <InviteState
        icon={<IconMail size={18} />}
        title="Verifying company access"
        body="Checking the accepted company context before continuing. The invitation will not be accepted again."
      />
    );
  } else if (status === 'error') {
    body = (
      <InviteState
        tone="warn"
        icon={<IconInfo size={18} />}
        title="Invitation could not be accepted"
        body={safeInviteError(error)}
      >
        {shouldOfferAccountSwitch(error) && (
          <div style={accountSwitchStyle}>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: PALETTE.ink, fontSize: 13, fontWeight: 700 }}>Wrong signed-in account?</div>
              <div style={{ color: PALETTE.ink3, fontSize: 12.5, lineHeight: 1.5, marginTop: 3 }}>
                {session.user?.email
                  ? `You are signed in as ${session.user.email}.`
                  : 'Switch accounts and reopen this same invitation.'}
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={goToInviteLogin}>
              <IconLogout size={14} /> Use another account
            </Button>
          </div>
        )}
      </InviteState>
    );
  } else {
    body = (
      <InviteState
        icon={<IconMail size={18} />}
        title="Accepting invitation"
        body="Checking the invite and joining the company."
      />
    );
  }

  return (
    <main className="bl-brand-surface" style={pageStyle}>
      <Card style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 24 }}>
          <div style={{ display: 'grid', gap: 16 }}>
            <BrandIdentity detail="Company invitations" />
            <h1 style={titleStyle}>Company Invitation</h1>
          </div>
          <Pill tone={status === 'accepted' ? 'live' : ['error', 'context-error'].includes(status) ? 'warn' : 'neutral'}>
            {status === 'accepted' ? 'Accepted' : ['error', 'context-error'].includes(status) ? 'Needs attention' : 'Invite'}
          </Pill>
        </div>
        {body}
      </Card>
    </main>
  );
}

function goToInviteLogin() {
  window.location.href = `${loginHrefForCurrentLocation()}&logout=1`;
}

function InviteState({ icon, title, body, tone = 'neutral', children }) {
  const colors = {
    neutral: { bg: PALETTE.surfaceAlt, fg: PALETTE.ink3 },
    live: { bg: PALETTE.successSoft, fg: PALETTE.success },
    warn: { bg: PALETTE.warnSoft, fg: PALETTE.warn },
  };
  const color = colors[tone] || colors.neutral;

  return (
    <section style={{ display: 'grid', gap: 12 }}>
      <div style={{
        width: 38,
        height: 38,
        borderRadius: 999,
        display: 'grid',
        placeItems: 'center',
        background: color.bg,
        color: color.fg,
      }}>
        {icon}
      </div>
      <div>
        <h2 style={{ margin: 0, color: PALETTE.ink, fontSize: 20, letterSpacing: 0 }}>{title}</h2>
        <p style={{ margin: '8px 0 0', color: PALETTE.ink2, fontSize: 14, lineHeight: 1.55 }}>{body}</p>
      </div>
      {children}
    </section>
  );
}

const pageStyle = {
  minHeight: '100vh',
  display: 'grid',
  placeItems: 'center',
  padding: 'clamp(18px, 4vw, 40px)',
  background: PALETTE.bg,
  fontFamily: 'Hanken Grotesk, sans-serif',
};

const cardStyle = {
  width: 'min(100%, 560px)',
  borderRadius: 10,
  padding: 'clamp(20px, 4vw, 30px)',
};

const titleStyle = {
  margin: 0,
  fontFamily: 'Instrument Serif, serif',
  fontStyle: 'italic',
  fontSize: 34,
  fontWeight: 400,
  color: PALETTE.ink,
  letterSpacing: 0,
};

const accountSwitchStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  flexWrap: 'wrap',
  marginTop: 8,
  paddingTop: 16,
  borderTop: `1px solid ${PALETTE.lineSoft}`,
};
